import React, { useEffect, useState } from "react";
import { RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { admin, errText, fmtDate } from "../../lib/adminApi";

const tone = {
  accepted: "bg-slate-500/20 text-slate-300", sent: "bg-sky-500/20 text-sky-300", delivered: "bg-emerald-500/20 text-emerald-300",
  delayed: "bg-amber-500/20 text-amber-300", bounced: "bg-red-500/20 text-red-300", failed: "bg-red-500/20 text-red-300",
  suppressed: "bg-red-500/20 text-red-300", complained: "bg-red-500/20 text-red-300", send_failed: "bg-red-500/20 text-red-300",
};
const TYPES = ["career-confirmation", "career-internal", "contact-internal", "contact-acknowledgement", "alert"];
const sel = "bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-orange [&>option]:bg-[#0A1F44]";

export const EmailsPanel = () => {
  const [data, setData] = useState({ items: [], suppressions: [] });
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await admin.get("/admin/emails", { params: { email_type: type || undefined, status: status || undefined } });
      setData(r.data);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [type, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const unsuppress = async (email) => {
    try {
      await admin.delete(`/admin/suppressions/${encodeURIComponent(email)}`);
      toast.success(`${email} can receive automated emails again`);
      load();
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-emails">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <select data-testid="admin-emails-type" value={type} onChange={(e) => setType(e.target.value)} className={sel}><option value="">All types</option>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <select data-testid="admin-emails-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}><option value="">All statuses</option>{Object.keys(tone).map((s) => <option key={s}>{s}</option>)}</select>
        <button onClick={load} data-testid="admin-emails-refresh" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white ml-auto"><RefreshCw size={14} /> Refresh</button>
      </div>

      {data.suppressions?.length > 0 && (
        <div data-testid="admin-suppressions" className="mb-6 rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm">
          <p className="text-red-200 font-medium flex items-center gap-2"><ShieldOff size={15} /> {data.suppressions.length} address{data.suppressions.length > 1 ? "es" : ""} blocked from automated emails (bounce / complaint / suppression)</p>
          <ul className="mt-2 space-y-1">
            {data.suppressions.map((s) => (
              <li key={s.email} className="flex flex-wrap items-center gap-3 text-red-100/80 font-mono text-xs">{s.email} <span className="opacity-60">{s.reason}</span>
                <button onClick={() => unsuppress(s.email)} data-testid={`admin-unsuppress-${s.email}`} className="ml-auto text-xs underline hover:text-white">Allow again</button></li>
            ))}
          </ul>
        </div>
      )}

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="text-left font-mono text-[10px] tracking-wider uppercase text-slate-500 border-b border-white/10">
              <th className="px-4 py-3">Type</th><th className="px-4 py-3">Recipient</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Events</th><th className="px-4 py-3">Sent</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
                : data.items.length === 0 ? <tr><td colSpan={6} data-testid="admin-emails-empty" className="px-4 py-10 text-center text-slate-500">No emails recorded.</td></tr>
                : data.items.map((m) => (
                  <tr key={m.id} data-testid={`admin-email-row-${m.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3 text-slate-200">{m.email_type}<span className="block text-[11px] text-slate-500">{m.related_type} {m.related_id?.slice(0, 8)}</span></td>
                    <td className="px-4 py-3 text-slate-300 break-all">{m.recipient}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{m.provider}</td>
                    <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${tone[m.status] || tone.accepted}`}>{m.status}</span>{m.error && <span className="block text-[11px] text-red-300 mt-1">{m.error}</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{m.events?.length ? m.events.map((e) => e.type.replace("email.", "")).join(" → ") : "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{fmtDate(m.created_at)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">"accepted" = provider accepted the send request. "delivered" is only set from verified Resend webhook events.</p>
    </div>
  );
};

export default EmailsPanel;
